import type { Doc } from "building-types/doc";
import type { Formula } from "building-types/options/formula";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { EntityParent, OptionSetEntityRelations } from "building-types/options/optionSetEntityRelationsTypes";
import type { Mutable } from "common/utils/types";
import type { Value } from "expr-eval";
import { castOptionChoicesToExpressionValue, parseFormula } from "../../formula/formulaParsing";

export type DisabledReason = "IS_REDUNDANT" | "ENTITY_DISABLED";

export interface DisabledOptionSet {
  readonly optionSetId: string;
  readonly reason: DisabledReason;
}

/**
 * Evaluates whether a formula evaluates to true (disabled).
 */
function evaluateDisabledFormula(formula: Formula | null, evalChoices: Value): boolean {
  if (!formula) {
    return false;
  }
  try {
    return parseFormula(formula.formula).evaluate(evalChoices) === true;
  } catch {
    return false;
  }
}

/**
 * Checks if an option set is redundant for a child entity.
 * An option set is redundant if changing its value doesn't affect the disabled formula outcome.
 */
function isOptionSetRedundantForChild(
  childFormula: Formula,
  optionSetId: string,
  allOptionIds: string[],
  scratchChoices: Mutable<OptionChoices>,
  optionChoices: OptionChoices,
  redundancyCache: Map<string, boolean>,
): boolean {
  if (allOptionIds.length <= 1) {
    return true;
  }

  const cacheKey = `${childFormula.formula}\0${optionSetId}`;
  const cached = redundancyCache.get(cacheKey);
  if (cached !== undefined) {
    return cached;
  }

  let isRedundant = false;
  const savedChoice = optionChoices[optionSetId];
  try {
    const expr = parseFormula(childFormula.formula);

    scratchChoices[optionSetId] = allOptionIds[0];
    const firstResult = expr.evaluate(scratchChoices as Value);

    isRedundant = true;
    for (let i = 1; i < allOptionIds.length; i++) {
      scratchChoices[optionSetId] = allOptionIds[i];
      if (expr.evaluate(scratchChoices as Value) !== firstResult) {
        isRedundant = false;
        break;
      }
    }
  } catch {
    isRedundant = false;
  } finally {
    if (savedChoice === undefined) {
      delete scratchChoices[optionSetId];
    } else {
      scratchChoices[optionSetId] = savedChoice;
    }
  }

  redundancyCache.set(cacheKey, isRedundant);
  return isRedundant;
}

/**
 * Checks if any entity in the parent chain has a disabled formula that evaluates to true.
 */
function isParentChainDisabled(
  current: EntityParent | null,
  evalChoices: Value,
  parentChainDisabledCache: Map<EntityParent | null, boolean>,
): boolean {
  const cached = parentChainDisabledCache.get(current);
  if (cached !== undefined) {
    return cached;
  }

  let disabled = false;
  let breakNode: EntityParent | undefined;
  let node: EntityParent | null = current;
  while (node) {
    const nodeCached = parentChainDisabledCache.get(node);
    if (nodeCached !== undefined) {
      disabled = nodeCached;
      breakNode = node;
      break;
    }
    if (evaluateDisabledFormula(node.expr, evalChoices)) {
      disabled = true;
      breakNode = node;
      break;
    }
    node = node.parent;
  }

  // Cache only the prefix from current down to breakNode. When a disabling ancestor is found,
  // do not cache nodes above it — their chains may still be enabled when queried directly.
  node = current;
  while (node) {
    if (parentChainDisabledCache.has(node)) {
      break;
    }
    parentChainDisabledCache.set(node, disabled);
    if (breakNode !== undefined && node === breakNode) {
      break;
    }
    node = node.parent;
  }

  if (current === null) {
    parentChainDisabledCache.set(null, disabled);
  }

  return disabled;
}

/**
 * Evaluates option set dependencies based purely on formula evaluation.
 */
export function evaluateOptionDependencies(
  relations: OptionSetEntityRelations,
  optionSets: Doc["optionSets"],
  optionChoices: OptionChoices,
): DisabledOptionSet[] {
  const disabledOptionSets: DisabledOptionSet[] = [];

  if (Object.keys(optionChoices).length === 0) {
    return disabledOptionSets;
  }

  const evalChoices = castOptionChoicesToExpressionValue(optionChoices);
  const scratchChoices: Mutable<OptionChoices> = { ...optionChoices };
  const redundancyCache = new Map<string, boolean>();
  const parentChainDisabledCache = new Map<EntityParent | null, boolean>();

  for (const [optionSetId, hierarchies] of Object.entries(relations.optionSetIdToEntityHierarchies)) {
    const optionSet = optionSets[optionSetId];
    if (!optionSet) {
      continue;
    }

    const allOptionIds = Object.keys(optionSet.options);
    let allParentsDisabled = true;
    let isFullyRedundant = true;
    let hasAnyChildren = false;

    for (const hierarchy of Object.values(hierarchies)) {
      const parentChainDisabled = isParentChainDisabled(hierarchy.parent, evalChoices, parentChainDisabledCache);

      if (parentChainDisabled) {
        // Parent is disabled, skip redundancy check for this hierarchy
      } else {
        allParentsDisabled = false;

        for (const child of Object.values(hierarchy.children)) {
          hasAnyChildren = true;
          if (child.expr) {
            const isRedundant = isOptionSetRedundantForChild(
              child.expr,
              optionSetId,
              allOptionIds,
              scratchChoices,
              optionChoices,
              redundancyCache,
            );
            if (!isRedundant) {
              isFullyRedundant = false;
              break;
            }
          } else {
            isFullyRedundant = false;
            break;
          }
        }

        if (!isFullyRedundant) {
          break;
        }
      }
    }

    if (allParentsDisabled) {
      disabledOptionSets.push({
        optionSetId,
        reason: "ENTITY_DISABLED",
      });
    }
    if (hasAnyChildren && isFullyRedundant) {
      disabledOptionSets.push({
        optionSetId,
        reason: "IS_REDUNDANT",
      });
    }
  }

  return disabledOptionSets;
}
